import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './client'

/**
 * The site programme: activities, their dependencies, and the readiness of each one.
 *
 * <p>Schedule figures are computed server-side on every read, so nothing here is cached
 * beyond the query itself — a duration change has to move every downstream date, and a
 * stale critical path is worse than none.</p>
 */

export type ActivityStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'DELAYED' | 'ON_HOLD'
export type DependencyType = 'FS' | 'SS' | 'FF'

export interface WorkItemProgress {
  workItemId: string
  code: string
  description: string
  uomCode?: string
  totalPlannedQuantity: number
  completedQuantity: number
  percentComplete?: number
}

export interface DrawingNeed {
  drawingId: string
  drawingNumber: string
  title: string
  status: string
  received: boolean
  daysPending?: number
}

export interface MaterialStatus {
  materialId: string
  materialCode: string
  materialName: string
  uomCode?: string
  requiredQuantity: number
  receivedQuantity: number
  shortageQuantity: number
}

export interface LabourStatus {
  labourCategoryId: string
  labourCategoryName: string
  requiredWorkers?: number
  recentWorkers?: number
  recentOn?: string
  shortfall?: number
}

export interface ActivityNode {
  id: string
  code: string
  name: string
  description?: string
  workPackageId?: string
  workPackageName?: string
  durationDays: number
  milestone: boolean
  status: ActivityStatus
  plannedStartDate?: string
  actualStartDate?: string
  actualFinishDate?: string
  earliestStart: number
  earliestFinish: number
  latestStart: number
  latestFinish: number
  totalFloat: number
  critical: boolean
  scheduledStart: string
  scheduledFinish: string
  depth: number
  percentComplete?: number
  behindSchedule: boolean
  workItems: WorkItemProgress[]
  drawings: DrawingNeed[]
  materials: MaterialStatus[]
  labour: LabourStatus[]
  blockers: string[]
  predecessorIds: string[]
}

export interface DependencyLink {
  id: string
  predecessorId: string
  predecessorCode: string
  successorId: string
  successorCode: string
  dependencyType: DependencyType
  lagDays: number
  critical: boolean
}

export interface NetworkSummary {
  activities: number
  completed: number
  inProgress: number
  notStarted: number
  criticalCount: number
  blockedCount: number
  durationDays: number
  start: string
  finish: string
  percentComplete?: number
}

export interface NetworkResponse {
  siteId: string
  siteCode: string
  siteName: string
  startDate: string
  summary: NetworkSummary
  activities: ActivityNode[]
  dependencies: DependencyLink[]
  warnings: string[]
}

export interface ActivityRequest {
  code: string
  name: string
  description?: string
  workPackageId?: string
  durationDays?: number
  milestone?: boolean
  plannedStartDate?: string | null
  status?: ActivityStatus
  workItemIds?: string[]
  drawingIds?: string[]
  materials?: { materialId: string; requiredQuantity?: number }[]
  labour?: { labourCategoryId: string; requiredWorkers?: number }[]
}

export function useNetwork(siteId?: string) {
  return useQuery({
    enabled: Boolean(siteId),
    queryKey: ['network', siteId],
    queryFn: async () => {
      const { data } = await api.get<NetworkResponse>(`/sites/${siteId}/network`)
      return data
    },
  })
}

function useNetworkMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['network'] })
    },
  })
}

export function useCreateActivity(siteId: string) {
  return useNetworkMutation(async (body: ActivityRequest) => {
    const { data } = await api.post<ActivityNode>(`/sites/${siteId}/activities`, body)
    return data
  })
}

export function useUpdateActivity() {
  return useNetworkMutation(async ({ id, body }: { id: string; body: ActivityRequest }) => {
    const { data } = await api.put<ActivityNode>(`/activities/${id}`, body)
    return data
  })
}

export function useDeleteActivity() {
  return useNetworkMutation(async (id: string) => {
    await api.delete(`/activities/${id}`)
  })
}

export function useLinkActivity() {
  return useNetworkMutation(
    async ({
      successorId,
      predecessorId,
      dependencyType,
      lagDays,
    }: {
      successorId: string
      predecessorId: string
      dependencyType?: DependencyType
      lagDays?: number
    }) => {
      const { data } = await api.post<DependencyLink>(`/activities/${successorId}/dependencies`, {
        predecessorId,
        dependencyType,
        lagDays,
      })
      return data
    },
  )
}

export function useUnlinkActivity() {
  return useNetworkMutation(async (dependencyId: string) => {
    await api.delete(`/activities/dependencies/${dependencyId}`)
  })
}

/** Drawings on the register for a site, for linking to an activity. */
export function useSiteDrawings(siteId?: string) {
  return useQuery({
    enabled: Boolean(siteId),
    queryKey: ['drawings', 'for-site', siteId],
    queryFn: async () => {
      const { data } = await api.get<{
        content: { id: string; drawingNumber: string; title: string; status: string }[]
      }>('/drawings', { params: { siteId, page: 0, size: 200 } })
      return data.content
    },
    staleTime: 5 * 60 * 1000,
  })
}
